import { createStore, applyMiddleware } from "redux";
import rootReducer from "./reducer.js";
import { composeWithDevTools } from "redux-devtools-extension";
import {
  createStateSyncMiddleware,
  initStateWithPrevTab,
} from "redux-state-sync";
import { loadFilterState, saveFilterState, validateFilterState, mergeWithUpdateChannel } from "./utils/statePersistence.js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

// Enhanced logger middleware with better state management
const loggerMiddleware = (storeAPI) => (next) => (action) => {
  console.log("dispatching", action);
  let result = next(action);
  console.log("next state", storeAPI.getState());
  
  // Only persist filter-related state, not the entire store
  const currentState = storeAPI.getState();
  const filterState = currentState.filter;
  
  // Use the utility function to save state (including sliderCollapsed)
  saveFilterState(filterState);
  
  return result;
};

// Configuration for state sync - exclude filter actions to prevent conflicts
const config = {
  blacklist: [
    "filter/updateDateFilter",
    "filter/updateMarketplace", 
    "filter/updateInventoryMarketplace",
    "filter/updateQuickInvMarketplace",
    "filter/updatePoInvMarketplace",
    "filter/updateReviewFilter",
    "filter/updateSliderFilter"
  ]
};

const middlewares = [createStateSyncMiddleware(config)];

const middlewareEnhancer = composeWithDevTools(
  applyMiddleware(loggerMiddleware, ...middlewares)
);

// Load persisted state using utility function
const persistedState = loadFilterState();
let initialState = undefined;

if (persistedState) {
  // updateChannel from localStorage to ensure channels are synced
  let mergedState = mergeWithUpdateChannel(persistedState);
  
  // Validate the merged state
  if (validateFilterState(mergedState)) {
    // Remove metadata fields before setting initial state
    const { lastUpdated, tabId, ...cleanState } = mergedState;
    // Restore sliderCollapsed from localStorage if available, otherwise use default false
    initialState = { 
      filter: { 
        ...cleanState, 
        sliderCollapsed: cleanState.sliderCollapsed !== undefined ? cleanState.sliderCollapsed : false 
      } 
    };
  } else {
    // If validation still fails, try to use updateChannel as fallback for marketplace
    try {
      const updateChannel = JSON.parse(localStorage.getItem("updateChannel"));
      if (updateChannel && Array.isArray(updateChannel) && updateChannel.length > 0) {
        // valid state with updateChannel and dates 
        const { lastUpdated, tabId, ...cleanState } = mergedState;
        const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
        const isValidDate = (date) => date && dateRegex.test(date);
        
        const fallbackState = {
          ...cleanState,
          marketplace: updateChannel,
          // Use persisted dates if valid, otherwise use defaults
          startDate: (cleanState.startDate && isValidDate(cleanState.startDate)) 
            ? cleanState.startDate 
            : dayjs().startOf("month").utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
          endDate: (cleanState.endDate && isValidDate(cleanState.endDate))
            ? cleanState.endDate
            : dayjs().endOf("month").utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
          // Restore sliderCollapsed from localStorage if available, otherwise use default false
          sliderCollapsed: cleanState.sliderCollapsed !== undefined ? cleanState.sliderCollapsed : false
        };
        initialState = { filter: fallbackState };
      }
    } catch (error) {
      console.warn('Failed to create fallback state:', error);
    }
  }
}

// Create store with persisted state
const store = createStore(
  rootReducer, 
  initialState,
  middlewareEnhancer
);

// Initialize state sync
initStateWithPrevTab(store);

export default store;
